import { authAPI } from './authApi';
import { createServerError } from './apiError';
import { resolveApiBaseUrl } from './apiBase';

const API_BASE_URL = resolveApiBaseUrl();

export interface ACLRedundancyInfo {
    redundant: boolean;
    reasonCode: string | null;
    message: string | null;
    supersedesBy?: string;
}

export interface ACLPermission {
    uid: string;
    whoType: 'UID' | 'GROUPID' | 'ROLE';
    who: string;
    action?: ACLAction;
    actions?: ACLAction[];
    whatType: 'ItemId' | 'CollectionId' | 'ItemIdSet' | 'CollectionIdSet' | 'AllItemsInCollectionId' | 'FormId' | 'FormIdSet' | 'AllFormsInCollectionId' | 'ConnectorKey';
    what: string | string[];
    includeFields?: string[];
    excludeFields?: string[];
    created: string;
    updated: string;
    createdBy?: string;
    updatedBy?: string;
    source?: string;
    aclProfile?: string;
    redundancy?: ACLRedundancyInfo | null;
}

export type ACLPermissionListMeta = {
    policyModel: string;
    generatedAt: string;
};

export function getPermissionActions(p: ACLPermission): ACLAction[] {
    if (Array.isArray(p.actions) && p.actions.length > 0) {
        return [...p.actions];
    }
    if (p.action) {
        return [p.action];
    }
    return [];
}

export type ACLAction =
    | 'view' | 'list' | 'listItems' | 'relate'
    | 'create' | 'create_collection' | 'read' | 'update' | 'delete'
    | 'delegate'
    | 'publish' | 'archive' | 'share'
    | 'field_read' | 'field_update'
    | 'edit_collection_name'
    | 'edit_collection_name_suggestion'
    | 'view_collection_advanced'
    | 'public_export'
    | 'public_publish_collection'
    | 'public_expose_fields'
    | 'public_form'
    | 'all_users_access_collection'
    | 'all_users_expose_fields'
    | 'create_form'
    | 'read_form'
    | 'update_form'
    | 'delete_form';

export type FormConfigAction = 'create_form' | 'read_form' | 'update_form' | 'delete_form';

export const FORM_CONFIG_ACTIONS: FormConfigAction[] = [
    'create_form',
    'read_form',
    'update_form',
    'delete_form'
];

export interface PermissionCheckRequest {
    action: ACLAction;
    targetType: 'item' | 'collection' | 'form';
    targetId: string;
    fieldKey?: string;
    /** Effective item owner UID (delegated owner or creator); preferred for item checks */
    itemOwnerId?: string;
    itemCreatorId?: string;
    /** Collection component UID (item checks, or linked collection for form checks) */
    collectionUid?: string;
}

export interface PermissionCheckResponse {
    allowed: boolean;
    reason: string;
    matchedPermissions?: ACLPermission[];
    layer?: string;
    policyModel?: string;
}

/**
 * Canonical action for ACL keys (matches backend `normalizeAclAction`).
 */
export function normalizeAclActionForPolicy(action: string): string {
    const a = String(action ?? '').trim();
    if (a.toLowerCase() === 'list') return 'listItems';
    return a;
}

/**
 * Map key for POST /acl/check-bulk results — must match apps/backend/src/services/aclService.js checkBulkPermissions.
 */
export function bulkPermissionResponseKey(request: PermissionCheckRequest): string {
    const ca = normalizeAclActionForPolicy(request.action);
    return `${ca}:${request.targetId}:${request.fieldKey ?? ''}`;
}

export interface AclAnalyzeResponse {
    generatedAt: string;
    policyModel: string;
    permissions: Array<{ uid: string; redundant: boolean; reasonCode: string | null; message: string | null; supersedesBy?: string }>;
    drafts: Array<{
        draft: Record<string, unknown>;
        redundant: boolean;
        reasonCode: string | null;
        message: string | null;
        supersedesBy?: string;
        isDraft?: boolean;
    }>;
}

export interface FieldPermissionsResponse {
    allowedFields: string[];
    deniedFields: string[];
}

class ACLAPI {
    private getAuthToken = (): string | null => {
        return authAPI.getIdToken();
    };

    private makeRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
        const token = this.getAuthToken();

        const config: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
        };

        const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

        if (response.status === 401) {
            throw new Error('Authentication required. Please login again.');
        }

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            const errorData = await response.json().catch(() => ({ message: 'Network error' }));
            throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        return await response.json();
    };

    /**
     * Check if user has permission to perform an action
     */
    async checkPermission(request: PermissionCheckRequest): Promise<PermissionCheckResponse> {
        try {
            const response = await this.makeRequest('/acl/check', {
                method: 'POST',
                body: JSON.stringify(request),
            });
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error checking permission:', error);
            throw error;
        }
    }

    /**
     * Bulk check multiple permissions at once
     */
    async checkBulkPermissions(requests: PermissionCheckRequest[]): Promise<Record<string, boolean>> {
        try {
            const response = await this.makeRequest('/acl/check-bulk', {
                method: 'POST',
                body: JSON.stringify({ requests }),
            });
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error checking bulk permissions:', error);
            throw error;
        }
    }

    /**
     * Which export field keys are visible to unauthenticated public (/public/exports), matching runtime ACL.
     */
    async getPublicJsonFeedFieldFlags(
        sourceComponentUid: string,
        fieldKeys: string[]
    ): Promise<Record<string, boolean>> {
        const response = await this.makeRequest('/acl/public-json-feed-field-flags', {
            method: 'POST',
            body: JSON.stringify({ sourceComponentUid, fieldKeys }),
        });
        return response.data as Record<string, boolean>;
    }

    /**
     * Get field-level permissions for a collection
     */
    async getFieldPermissions(collection: string, action: 'field_read' | 'field_update'): Promise<FieldPermissionsResponse> {
        try {
            const response = await this.makeRequest(`/acl/fields/${collection}/${action}`);
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error getting field permissions:', error);
            throw error;
        }
    }

    /**
     * Get all permissions (admin only). Pass includeRedundancy for analyzer hints.
     */
    async getAllPermissions(options?: {
        includeRedundancy?: boolean;
    }): Promise<{ data: ACLPermission[]; meta?: ACLPermissionListMeta }> {
        try {
            const qs =
                options?.includeRedundancy === true ? '?includeRedundancy=1' : '';
            const response = await this.makeRequest(`/acl/permissions${qs}`);
            return {
                data: response.data,
                meta: response.meta,
            };
        } catch (error: any) {
            console.error('ACL API: Error getting all permissions:', error);
            throw error;
        }
    }

    async analyzeAcl(body: { drafts?: Array<Record<string, unknown>> }): Promise<AclAnalyzeResponse> {
        try {
            const response = await this.makeRequest('/acl/analyze', {
                method: 'POST',
                body: JSON.stringify(body || {}),
            });
            return response.data;
        } catch (error: any) {
            console.error('ACL API: analyze error:', error);
            throw error;
        }
    }

    /**
     * Get permission by UID
     */
    async getPermissionByUid(uid: string): Promise<ACLPermission> {
        try {
            const response = await this.makeRequest(`/acl/permissions/${uid}`);
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error getting permission:', error);
            throw error;
        }
    }

    /**
     * Create new permission (admin only)
     */
    async createPermission(
        permission: Omit<ACLPermission, 'uid' | 'created' | 'updated' | 'createdBy' | 'updatedBy' | 'redundancy'>
    ): Promise<ACLPermission> {
        try {
            const response = await this.makeRequest('/acl/permissions', {
                method: 'POST',
                body: JSON.stringify(permission),
            });
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error creating permission:', error);
            throw error;
        }
    }

    /**
     * Update existing permission (admin only)
     */
    async updatePermission(uid: string, updates: Partial<Omit<ACLPermission, 'uid' | 'created' | 'updated' | 'createdBy' | 'updatedBy'>>): Promise<ACLPermission> {
        try {
            const response = await this.makeRequest(`/acl/permissions/${uid}`, {
                method: 'PUT',
                body: JSON.stringify(updates),
            });
            return response.data;
        } catch (error: any) {
            console.error('ACL API: Error updating permission:', error);
            throw error;
        }
    }

    /**
     * Delete permission (admin only)
     */
    async deletePermission(uid: string): Promise<void> {
        try {
            await this.makeRequest(`/acl/permissions/${uid}`, {
                method: 'DELETE',
            });
        } catch (error: any) {
            console.error('ACL API: Error deleting permission:', error);
            throw error;
        }
    }
}

export const aclAPI = new ACLAPI();
export default aclAPI;
